import { Accordion } from '@ps/isp-web-component-library';
import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import classnames from 'clsx';
import React from 'react';
import { useField } from 'react-final-form';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import {
  getIsAvailableOpportunity,
  getIsMultipleAvailableOpportunity,
  getIsSingleAvailableOpportunity,
  getIsSingleOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import useIsMultipleStartingPriceOpportunity from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useIsMultipleStartingPriceOpportunity';
import { calculatePotentialReturns } from '@swf/layouts/SportsLayout/components/BetSlip/utils/calculatePotentialReturns';
import { calculateStake } from '@swf/layouts/SportsLayout/components/BetSlip/utils/calculateStake';
import { calculateTotalPotentialReturns } from '@swf/layouts/SportsLayout/components/BetSlip/utils/calculateTotalPotentialReturns';
import getIsDefaultStartingPriceSelected from '@swf/layouts/SportsLayout/components/BetSlip/utils/getIsDefaultStartingPriceSelected';

import {
  TXTWCL_Bet_Details,
  TXTWCL_Each_Way,
  TXTWCL_If_All_Selections_Place,
  TXTWCL_If_All_Selections_Win,
  TXTWCL_If_Selection_Places,
  TXTWCL_If_Selection_Wins,
  TXTWCL_Returns,
  TXTWCL_Total_Stake,
  TXTWCL_Winning_Outcomes,
  TXTWCL_n_a
} from './BetDetails.messages.json';
import styles from './BetDetails.scss';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const BetDetails: React.FC<Props> = ({ opportunity }) => {
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const isMultipleStartingPriceOpportunity = useIsMultipleStartingPriceOpportunity(opportunity);

  if (!input.value?.eachWaySelected) {
    return null;
  }

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isMultipleAvailableOpportunity = getIsMultipleAvailableOpportunity(opportunity);
  const isSingleOpportunity = getIsSingleOpportunity(opportunity);
  const isStartingPriceSelected =
    input.value.startingPriceSelected ?? getIsDefaultStartingPriceSelected(opportunity);
  const totalStake = calculateStake(
    input.value.stakePerLineParsed ?? 0,
    isAvailableOpportunity ? opportunity.numberOfLines : 0,
    input.value.eachWaySelected
  );
  const winPotentialReturn = isAvailableOpportunity
    ? calculateTotalPotentialReturns({
        stakePerLine: input.value.stakePerLineParsed ?? 0,
        placeReturnFactor: opportunity.placeReturnFactor,
        returnFactor: opportunity.returnFactor,
        numberOfLines: opportunity.numberOfLines,
        eachWaySelected: input.value.eachWaySelected,
        isFreeBet: Boolean(input.value.freeBet?.length)
      })
    : 0;
  const placePotentialReturn = calculatePotentialReturns({
    stake: input.value.stakePerLineParsed ?? 0,
    returnFactor: isAvailableOpportunity ? (opportunity.placeReturnFactor ?? 0) : 0,
    numberOfLines: isAvailableOpportunity ? opportunity.numberOfLines : 0,
    isFreeBet: Boolean(input.value.freeBet?.length)
  });

  let shouldDisplayPotentialReturn = true;
  if (isSingleAvailableOpportunity) {
    shouldDisplayPotentialReturn = Boolean(!isStartingPriceSelected && opportunity.winRunnerOdds);
  } else if (isMultipleAvailableOpportunity) {
    shouldDisplayPotentialReturn = !isMultipleStartingPriceOpportunity;
  }

  return (
    <div className={styles.wrapper}>
      <Accordion variant="lightMedium" isInitiallyExpanded={false}>
        <Accordion.Header>
          <Translation {...TXTWCL_Bet_Details} />
        </Accordion.Header>
        <Accordion variant="lightContrast">
          <Accordion.Header>
            <Translation {...TXTWCL_Each_Way} />
          </Accordion.Header>
          <Accordion.Body>
            <div className={styles.eachWayWrapper}>
              <span className={styles.rowOne}>
                <Translation {...TXTWCL_Total_Stake} />
              </span>
              <span className={classnames(styles.rowOne, styles.rightColumn)}>
                <CurrencyAmount value={totalStake} />
              </span>
              <span className={styles.heading}>
                <Translation {...TXTWCL_Winning_Outcomes} />
              </span>
              <span className={classnames(styles.heading, styles.rightColumn)}>
                <Translation {...TXTWCL_Returns} />
              </span>
              <span>
                {isSingleOpportunity ? (
                  <Translation {...TXTWCL_If_Selection_Wins} />
                ) : (
                  <Translation {...TXTWCL_If_All_Selections_Win} />
                )}
              </span>
              <span className={styles.rightColumn}>
                {shouldDisplayPotentialReturn ? (
                  <CurrencyAmount value={winPotentialReturn} />
                ) : (
                  <Translation {...TXTWCL_n_a} />
                )}
              </span>

              <span>
                {isSingleOpportunity ? (
                  <Translation {...TXTWCL_If_Selection_Places} />
                ) : (
                  <Translation {...TXTWCL_If_All_Selections_Place} />
                )}
              </span>
              <span className={styles.rightColumn}>
                {shouldDisplayPotentialReturn ? (
                  <CurrencyAmount value={placePotentialReturn} />
                ) : (
                  <Translation {...TXTWCL_n_a} />
                )}
              </span>
            </div>
          </Accordion.Body>
        </Accordion>
      </Accordion>
    </div>
  );
};

export default BetDetails;
